import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Archive, ArchiveRestore, Trash2, Gift, FileText, Filter, Globe, Network } from 'lucide-react';

/*
  C1 — Archive page. One screen listing the ARCHIVED rows of every
  soft-deletable entity (offers, landing pages, streams, traffic sources,
  affiliate networks) with two actions per row:

    * Restore            → POST /{entity}/{id}/restore
    * Delete permanently → DELETE /{entity}/{id}/purge (behind ConfirmDialog)

  The backend only exposes ?include_archived=true (no archived-only flag,
  campaign-list parity), so each section fetches the full list and filters
  client-side to is_archived. Streams are nested under campaigns, so the
  streams section walks GET /campaigns?include_archived=true first and then
  fans out to GET /campaigns/{id}/streams?include_archived=true.
*/

const onlyArchived = (rows) => (rows || []).filter((r) => r && r.is_archived);

// Section descriptors. `base` is both the restore/purge URL prefix; the
// streams endpoints live under /streams/{id} even though the LIST is nested
// under campaigns (C1 backend).
const SECTIONS = [
  { key: 'offers', titleKey: 'Offers', icon: Gift, base: '/offers' },
  { key: 'landingPages', titleKey: 'Landing pages', icon: FileText, base: '/landing-pages' },
  { key: 'streams', titleKey: 'Streams', icon: Filter, base: '/streams' },
  { key: 'trafficSources', titleKey: 'Traffic sources', icon: Globe, base: '/traffic-sources' },
  { key: 'affiliateNetworks', titleKey: 'Affiliate networks', icon: Network, base: '/affiliate-networks' },
];

const fetchArchivedList = async (path) => {
  const response = await axios.get(`${API}${path}`, { params: { include_archived: true } });
  return onlyArchived(response.data);
};

const fetchArchivedStreams = async () => {
  const campaignsResponse = await axios.get(`${API}/campaigns`, { params: { include_archived: true } });
  const campaigns = campaignsResponse.data || [];
  const perCampaign = await Promise.all(
    campaigns.map((campaign) =>
      axios
        .get(`${API}/campaigns/${campaign.id}/streams`, { params: { include_archived: true } })
        .then((r) => onlyArchived(r.data).map((s) => ({ ...s, campaign_name: campaign.name })))
        // A single unreadable campaign must not sink the whole section.
        .catch(() => [])
    )
  );
  return [].concat(...perCampaign);
};

const FETCHERS = {
  offers: () => fetchArchivedList('/offers'),
  landingPages: () => fetchArchivedList('/landing-pages'),
  streams: fetchArchivedStreams,
  trafficSources: () => fetchArchivedList('/traffic-sources'),
  affiliateNetworks: () => fetchArchivedList('/affiliate-networks'),
};

export default function ArchivePage() {
  const { t } = useT();
  const [data, setData] = useState({
    offers: [],
    landingPages: [],
    streams: [],
    trafficSources: [],
    affiliateNetworks: [],
  });
  const [loading, setLoading] = useState(true);
  const [purgeTarget, setPurgeTarget] = useState(null); // { section, row }

  const loadAll = async () => {
    setLoading(true);
    let failed = false;
    const entries = await Promise.all(
      SECTIONS.map(async (section) => {
        try {
          return [section.key, await FETCHERS[section.key]()];
        } catch (error) {
          console.error(`Error loading archived ${section.key}:`, error);
          failed = true;
          return [section.key, []];
        }
      })
    );
    const next = {};
    entries.forEach(([key, rows]) => { next[key] = rows; });
    setData(next);
    if (failed) toast.error(t('Error loading archive'));
    setLoading(false);
  };

  const reloadSection = async (sectionKey) => {
    try {
      const rows = await FETCHERS[sectionKey]();
      setData((prev) => ({ ...prev, [sectionKey]: rows }));
    } catch (error) {
      console.error(`Error loading archived ${sectionKey}:`, error);
      toast.error(t('Error loading archive'));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  const handleRestore = async (section, row) => {
    try {
      await axios.post(`${API}${section.base}/${row.id}/restore`);
      toast.success(t('Restored'));
      await reloadSection(section.key);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error restoring'));
    }
  };

  const handlePurge = async () => {
    if (!purgeTarget) return;
    const { section, row } = purgeTarget;
    try {
      await axios.delete(`${API}${section.base}/${row.id}/purge`);
      setPurgeTarget(null);
      toast.success(t('Deleted permanently'));
      await reloadSection(section.key);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Error deleting'));
    }
  };

  const formatArchivedAt = (value) => (value ? new Date(value).toLocaleString() : '—');

  const totalArchived = SECTIONS.reduce((sum, s) => sum + (data[s.key] || []).length, 0);

  if (loading) {
    return (
      <div className="p-8" data-testid="archive-skeleton">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="archive-view">
      <XpnsrPageHeader
        kicker={t('Workspace')}
        title={
          <>
            {t('Archive')} <em>({totalArchived})</em>
          </>
        }
        subtitle={t('Archived items are hidden from your lists. Restore them or delete them permanently.')}
      />

      {totalArchived === 0 ? (
        <Card>
          <CardContent style={{ padding: 0 }}>
            <XpnsrEmpty
              icon={Archive}
              title={t('No archived items')}
              subtitle={t('Archived items are hidden from your lists. Restore them or delete them permanently.')}
            />
          </CardContent>
        </Card>
      ) : (
        SECTIONS.map((section) => {
          const rows = data[section.key] || [];
          const SectionIcon = section.icon;
          return (
            <Card key={section.key} data-testid={`archive-section-${section.key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <SectionIcon className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg">{t(section.titleKey)}</CardTitle>
                  <Badge variant="secondary">{rows.length}</Badge>
                </div>
              </CardHeader>
              <CardContent style={{ paddingTop: 0 }}>
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">{t('No archived items')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('Name')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t('Archived at')}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t('Actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.id} data-testid={`archive-row-${row.id}`}>
                            <TableCell className="whitespace-nowrap">
                              <div className="font-medium">{row.name}</div>
                              {section.key === 'streams' && row.campaign_name && (
                                <div className="text-xs text-muted-foreground">{row.campaign_name}</div>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                              {formatArchivedAt(row.archived_at)}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={t('Restore')}
                                  title={t('Restore')}
                                  onClick={() => handleRestore(section, row)}
                                  data-testid={`archive-restore-${row.id}`}
                                >
                                  <ArchiveRestore className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={t('Delete permanently')}
                                  title={t('Delete permanently')}
                                  onClick={() => setPurgeTarget({ section, row })}
                                  data-testid={`archive-purge-${row.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      <ConfirmDialog
        open={!!purgeTarget}
        onOpenChange={(open) => { if (!open) setPurgeTarget(null); }}
        title={t('Delete permanently')}
        description={t('This will permanently delete "{name}". This action cannot be undone.').replace('{name}', purgeTarget?.row?.name || '')}
        confirmLabel={t('Delete permanently')}
        onConfirm={handlePurge}
      />
    </div>
  );
}
